import React from 'react'
import get from 'lodash-es/get'
import isEmpty from 'lodash-es/isEmpty'

import { WithStyles, createStyles, withStyles, Theme } from '@material-ui/core/styles'

import Typography from '@material-ui/core/Typography'

import { Action } from 'types'
import { GamesResponse, IGame } from 'protos'

import T from 'components/FormattedMessage'
import Button from 'components/Button'
import GameCard from 'components/GameCard'
import QuizIntro from 'components/GameCard/components/QuizIntro'
import QuizCard from 'components/GameCard/components/QuizCard'
import FeaturedMatch from 'components/FeaturedMatch'
import ActionContext from 'components/ActionContext'
import LinkButtonBase from 'components/LinkButtonBase'
import { QuizToggleContext } from 'contexts/QuizToggleContext'
import { QuizAnswersProvider, getPersistedQuestionKey } from 'contexts/QuizAnswersContext'

import footballIconUrl from 'assets/images/football.svg'

interface Props {
  activeGameList: Action<GamesResponse | null>
  createGameUrl?: string
  quizIntoAcked?: boolean
  acknowledgeQuizIntro: () => Promise<void>
  getUrlForGameDetails: (gameId: string) => string
  saveQuizAnswer: (gameId: string, questionId: string, answerId: string) => Promise<void>
}

const styles = (theme: Theme) => createStyles({
  cardButtonRoot: {
    minWidth: 0,
    display: 'block',
    marginTop: theme.spacing() * 3,
    marginLeft: 20,
    marginRight: 20,

    '&:last-child': {
      marginBottom: theme.spacing() * 3,
    }
  }
})

type InjectedProps = Props & WithStyles<typeof styles>

class ActiveGamesTab extends React.Component<InjectedProps> {
  private renderEmptyState = () => {
    if (!this.props.activeGameList.result) {
      return null
    }

    return (
      <>
        <div style={{ margin: '24px auto', padding: 16, border: '1px dashed lightgrey' }}>
          <img alt="" src={footballIconUrl} style={{ display: 'block', width: 40, height: 40, margin: '8px auto' }} />
          <Typography align="center" variant="h5">
            <T id="game_empty_title" />
          </Typography>
          <Typography align="center" variant="subtitle1" color="textSecondary" style={{ margin: '16px auto' }}>
            <T id="game_empty_subtitle" />
          </Typography>
          <Button
            color="primary"
            variant="contained"
            to={this.props.createGameUrl}
            style={{ width: 200, margin: 'auto', display: 'block', textAlign: 'center' }}
          >
            <T id="centerbutton_play" />
          </Button>
        </div>
      </>
    )
  }

  private renderGameList = () => {
    const { isQuizClosedByUser, isQuizOpenedByUser, closeQuiz, openQuiz } = React.useContext(QuizToggleContext)
    const { activeGameList, quizIntoAcked, classes } = this.props
    const games = activeGameList!.result!.game

    return (
      <>
        {
          games.map((game) => {
            if (game.invitation) {
              return (
                <div key={game.gameId!} className={classes.cardButtonRoot}>
                  <GameCard game={game} />
                </div>
              )
            }

            const hasActiveQuiz = !!game.activeQuiz && !isEmpty(game.activeQuiz.question)

            if (!quizIntoAcked && hasActiveQuiz) {
              const firstQuestionImage = get(game, 'activeQuiz.question[0].iconUrl')

              return (
                <div key={game.gameId!} className={classes.cardButtonRoot}>
                  <QuizIntro
                    onClose={this.props.acknowledgeQuizIntro}
                    homeTeam={game!.match!.homeTeam!.name!}
                    awayTeam={game!.match!.awayTeam!.name!}
                    imageUrl={firstQuestionImage}
                  />
                </div>
              )
            }

            if (quizIntoAcked && hasActiveQuiz && !isQuizClosedByUser(game.gameId!)) {
              const hasUnansweredQuestion = game.activeQuiz!.question!.some(
                (question) => !isEmpty(question.answer) && !question.selectedAnswerId
              )

              if (hasUnansweredQuestion || isQuizOpenedByUser(game.gameId!)) {
                return (
                  <div key={game.gameId!} className={classes.cardButtonRoot}>
                    <QuizCard
                      onClose={() => { closeQuiz(game.gameId!) }}
                      onAnswer={this.props.saveQuizAnswer}
                      gameId={game!.gameId!}
                      homeTeam={game!.match!.homeTeam!.name!}
                      awayTeam={game!.match!.awayTeam!.name!}
                      questions={game.activeQuiz!.question!}
                    />
                  </div>
                )
              }
            }

            return (
              <LinkButtonBase
                key={game.gameId!}
                classes={{ root: classes.cardButtonRoot }}
                to={this.props.getUrlForGameDetails(game.gameId!)}
              >
                <GameCard
                  game={game}
                  displayQuizButton={hasActiveQuiz}
                  onCickDisplayQuizButton={() => { openQuiz(game.gameId!) }}
                />
              </LinkButtonBase>
            )
          })
        }
      </>
    )
  }

  private initializeQuestionAnswers = (games: IGame[]): { [questionId: string]: string } => {
    return games.reduce<{ [key: string]: string }>((previousAnswers, game) => {
      if (game.activeQuiz && game.activeQuiz.question) {
        for (const question of game.activeQuiz.question) {
          if (question.selectedAnswerId) {
            previousAnswers[getPersistedQuestionKey(game.gameId!, question.questionId!)] = question.selectedAnswerId
          }
        }
      }

      return previousAnswers
    }, {})
  }

  private renderContent = () => {
    const { activeGameList } = this.props

    if (!activeGameList.result) {
      return null
    }

    const games = activeGameList.result.game || []
    const answers = this.initializeQuestionAnswers(games)

    const { sponsored } = activeGameList.result

    return (
      <QuizAnswersProvider answers={answers}>
        {sponsored && sponsored.match && (
          <LinkButtonBase
            to={`/games/create/team?type=public&match=${sponsored.match.matchId}`}
            style={{ display: 'block' }}
          >
            <FeaturedMatch match={sponsored.match} />
          </LinkButtonBase>
        )}
        {games.length === 0 ? this.renderEmptyState() : this.renderGameList()}
      </QuizAnswersProvider>
    )
  }

  render() {
    const { activeGameList } = this.props

    return (
      <ActionContext
        isLoading={activeGameList.isLoading}
        errorCode={activeGameList.errorCode}
        component={this.renderContent}
        componentProps={{}}
      />
    )
  }
}

export default withStyles(styles)(ActiveGamesTab)
